import { seoLinks, absoluteUrl, ogImage } from "@/lib/seo";
import { InlineCta } from "@/components/site/InlineCta";
import ogHeroImage from "../../assets/preschool-play.webp";
import { createFileRoute, Link } from "@tanstack/react-router";

import { CtaBand, PageHero, Prose } from "../../components/site/Page";
import { GuideLinks } from "../../components/site/Guides";
import { KnowledgeHubNote } from "../../components/site/KnowledgeHubNote";
import { NewcomerNote } from "../../components/site/NewcomerNote";

const title = "Swedish preschool in Spain – how it works in practice";
const description =
  "A guide to Swedish preschool abroad: the pedagogy, the daily rhythm, language development, settling in and what to ask any preschool on the Costa del Sol.";

export const Route = createFileRoute("/en/swedish-preschool-in-spain")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "article" },
      { property: "og:url", content: absoluteUrl("/en/swedish-preschool-in-spain") },
      { name: "twitter:card", content: "summary_large_image" },
      ...ogImage("/og/forskola-v1.jpg"),
      { property: "og:locale", content: "en_GB" },
    ],
    links: seoLinks("/en/swedish-preschool-in-spain"),
  }),
  component: Page,
});

function Page() {
  return (
    <>
      <PageHero
        eyebrow="Guide"
        title="Swedish preschool in Spain"
        intro="What does it actually mean to let a small child start at a Swedish preschool abroad? Here is how the day looks, how several languages develop side by side, and the questions worth asking before you decide."
      />

      <NewcomerNote />

      <Prose>
        <h2>The same pedagogical foundation as at home</h2>
        <p>
          Swedish preschool pedagogy rests on the idea that children learn through play, care and
          exploration together with others – not through school-like lessons. The child is seen as
          capable and curious, and the adults' job is to provide a safe frame where the child dares
          to try, fail and try again. That is as true in Spain as in Sweden. The difference is the
          setting: the climate allows a far larger part of the day to be spent outdoors, and the
          group usually holds several languages and nationalities from the start.
        </p>
        <p>
          For a family that plans to move back home eventually, continuity is the strongest
          argument. The child stays within the same pedagogical tradition, with Swedish as the
          shared language of the group, and the transition back to a Swedish preschool or
          reception class is rarely dramatic. For families planning to stay, the argument is
          rather that the child keeps Swedish as a living everyday language, not only as a
          heritage language.
        </p>

        <h2>An ordinary day</h2>
        <p>
          The day follows a predictable rhythm, which suits small children: a calm arrival, circle
          time, free play and guided activities, a snack, outdoor time and rest. Activities revolve
          around language, creative work, movement, nature and everyday mathematics – counting,
          sorting, comparing. We work a great deal with stories, songs and rhymes, because they
          build vocabulary and speech sounds while being genuinely fun.
        </p>
        <p>
          Outdoor time is extensive. Through spring, autumn and winter it is usually pleasant to be
          outside for large parts of the day, while the hottest weeks call for shade, water play
          and adjusted timings. Sun protection, a cap and spare clothes are part of the bag all
          year round.
        </p>

        <h2>Three languages without pressure</h2>
        <p>
          Swedish is the base. Spanish and English arrive through songs, play, routines and
          recurring activities rather than formal teaching. Young children learn languages by
          hearing them used in situations that matter to them – at the table, in play, in the
          cloakroom.
        </p>
        <p>
          It is entirely normal for children to mix languages for a period, or for one language to
          go quiet before it suddenly appears. We do not chase early results. The goal is that the
          child finds several languages natural and undramatic, not something you can get wrong.
        </p>
      </Prose>

      <div className="mx-auto max-w-3xl px-5">
        <InlineCta locale="en" />
      </div>

      <Prose>

        <h2>Settling in</h2>
        <p>
          Settling in happens step by step at the child's pace, with a parent close at hand in the
          beginning. Many newly arrived families have a great deal going on at once – housing,
          authorities, work – which makes it all the more important that preschool becomes the part
          of life that feels stable first. We agree the approach with you based on the child's age
          and previous experience of preschool.
        </p>
        <p>
          We stay in close contact with guardians day to day: brief check-ins at drop-off and
          pick-up, and longer conversations when needed. If your child has allergies, particular
          needs or a history we should know about, we want to hear it from the start.
        </p>

        <h2>Questions to ask any preschool</h2>
        <ul>
          <li>Which curriculum do you follow, and how does it show in daily life?</li>
          <li>How many children are in the group, and how many adults work there?</li>
          <li>Which languages are spoken between children and staff during the day?</li>
          <li>What does settling in look like, and how long does it usually take?</li>
          <li>What is the outdoor environment like, and how is heat and sun handled?</li>
          <li>What is included in the fee – meals, outings, materials?</li>
          <li>How do you communicate with guardians day to day?</li>
        </ul>

        <h2>At our school</h2>
        <p>
          Our preschool welcomes children from the age of two and sits in the same secure setting as
          the school, which keeps the step up to reception class small. Read more about{" "}
          <Link className="text-primary link-quiet" to="/en/preschool">
            our preschool
          </Link>
          , see the{" "}
          <Link className="text-primary link-quiet" to="/en/fees">
            fees
          </Link>{" "}
          or get in touch and we will show you around.
        </p>
      </Prose>

      <KnowledgeHubNote resource="flyttchecklistan" locale="en" />

      <GuideLinks current="/svensk-forskola-i-spanien" locale="en" />
      <CtaBand />
      <div className="h-20" />
    </>
  );
}
